import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query,
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowLeftIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { mealDeskCompaniesService, mealDeskVendorsService, citiesService } from '../firebase/services';

const MealDeskCompaniesManager = () => {
  const [companies, setCompanies] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    pinCode: '',
    contactPerson: '',
    contactPhone: '',
    email: '',
    rotationMode: 'daily',
    approvedVendors: [],
    mealSchedule: {
      breakfast: '08:00',
      lunch: '13:00',
      dinner: '19:00'
    },
    vendorRotationByDay: {}
  });
  const [errors, setErrors] = useState({});

  // Rotation mode options
  const rotationModes = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'fixed', label: 'Fixed' },
    { value: 'customDays', label: 'Custom Days' }
  ];

  // Days of the week for custom rotation
  const daysOfWeek = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];

  // Load companies and vendors
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load companies with real-time updates
        const companiesQuery = query(collection(db, 'MealDeskCompanies'), orderBy('name'));
        const unsubscribeCompanies = onSnapshot(companiesQuery, (snapshot) => {
          const companiesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setCompanies(companiesData);
          setLoading(false);
        });

        // Load vendors
        const vendorsData = await mealDeskVendorsService.getAllVendors();
        setVendors(vendorsData);

        // Load cities and states
        const citiesData = await citiesService.getAllCities();
        setCities(citiesData);
        
        const statesData = await citiesService.getUniqueStates();
        setStates(statesData);

        return () => unsubscribeCompanies();
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      pinCode: '',
      contactPerson: '',
      contactPhone: '',
      email: '',
      rotationMode: 'daily',
      approvedVendors: [],
      mealSchedule: {
        breakfast: '08:00',
        lunch: '13:00',
        dinner: '19:00'
      },
      vendorRotationByDay: {}
    });
    setErrors({});
    setEditingCompany(null);
  };

  // Open drawer for add/edit
  const openDrawer = (company = null) => {
    if (company) {
      // Handle backward compatibility with old address field
      const oldAddress = company.address || '';
      const addressParts = oldAddress.split(',').map(part => part.trim());
      
      // Find matching city and state from Cities collection
      const cityState = findMatchingCityState(company.city || addressParts[2] || '', company.state || addressParts[3] || '');
      
      setFormData({
        name: company.name || '',
        address1: company.address1 || addressParts[0] || '',
        address2: company.address2 || addressParts[1] || '',
        city: cityState.city,
        state: cityState.state,
        pinCode: company.pinCode || addressParts[4] || '',
        contactPerson: company.contactPerson || '',
        contactPhone: company.contactPhone || '',
        email: company.email || '',
        rotationMode: company.rotationMode || 'daily',
        approvedVendors: company.approvedVendors || [],
        mealSchedule: company.mealSchedule || {
          breakfast: '08:00',
          lunch: '13:00',
          dinner: '19:00'
        },
        vendorRotationByDay: company.vendorRotationByDay || {}
      });
      setEditingCompany(company);
    } else {
      resetForm();
    }
    setShowDrawer(true);
  };

  // Close drawer
  const closeDrawer = () => {
    setShowDrawer(false);
    resetForm();
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle meal schedule changes
  const handleMealScheduleChange = (meal, time) => {
    setFormData(prev => ({
      ...prev,
      mealSchedule: {
        ...prev.mealSchedule,
        [meal]: time
      }
    }));
  };

  // Handle vendor rotation by day changes
  const handleVendorRotationChange = (day, vendorId) => {
    setFormData(prev => ({
      ...prev,
      vendorRotationByDay: {
        ...prev.vendorRotationByDay,
        [day]: vendorId
      }
    }));
  };

  // Get cities by selected state
  const getCitiesByState = (selectedState) => {
    return cities.filter(city => city.State === selectedState);
  };

  // Find matching city and state from Cities collection
  const findMatchingCityState = (cityName, stateName) => {
    const matchingCity = cities.find(city => 
      city.City === cityName && city.State === stateName
    );
    return matchingCity ? { city: cityName, state: stateName } : { city: '', state: '' };
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Company name is required';
    }

    if (!formData.address1.trim()) {
      newErrors.address1 = 'Address line 1 is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    if (!formData.pinCode.trim()) {
      newErrors.pinCode = 'Pin code is required';
    } else if (!/^[0-9]{6}$/.test(formData.pinCode)) {
      newErrors.pinCode = 'Invalid pin code format (6 digits)';
    }

    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = 'Contact person is required';
    }

    if (!formData.contactPhone.trim()) {
      newErrors.contactPhone = 'Contact phone is required';
    } else if (!/^[0-9+\-\s()]+$/.test(formData.contactPhone)) {
      newErrors.contactPhone = 'Invalid phone number format';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.approvedVendors.length === 0) {
      newErrors.approvedVendors = 'At least one approved vendor is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save company
  const saveCompany = async () => {
    if (!validateForm()) return;

    try {
      if (editingCompany) {
        await mealDeskCompaniesService.updateCompany(editingCompany.id, formData);
      } else {
        await mealDeskCompaniesService.addCompany(formData);
      }

      closeDrawer();
    } catch (error) {
      console.error('Error saving company:', error);
      alert('Error saving company. Please try again.');
    }
  };

  // Delete company
  const deleteCompany = async (companyId) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        await mealDeskCompaniesService.deleteCompany(companyId);
      } catch (error) {
        console.error('Error deleting company:', error);
        alert('Error deleting company. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => window.history.back()}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Kerblet MealDesk Customers</h1>
              <p className="text-sm text-gray-500">Manage meal service companies</p>
            </div>
          </div>
          <button
            onClick={() => openDrawer()}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Company
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Companies ({companies.length})</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rotation Mode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendors
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {companies.map((company) => (
                  <tr key={company.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{company.name}</div>
                        <div className="text-sm text-gray-500 flex items-center mt-1">
                          <MapPinIcon className="h-4 w-4 mr-1" />
                          {company.address1 || company.address ? (
                            <>
                              {company.address1 || company.address}
                              {company.address2 && `, ${company.address2}`}
                              {company.city && `, ${company.city}`}
                              {company.state && `, ${company.state}`}
                              {company.pinCode && ` - ${company.pinCode}`}
                            </>
                          ) : (
                            'No address provided'
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{company.contactPerson}</div>
                      <div className="text-sm text-gray-500">{company.contactPhone}</div>
                      <div className="text-sm text-gray-500">{company.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {rotationModes.find(mode => mode.value === company.rotationMode)?.label || company.rotationMode}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {company.approvedVendors?.length || 0} vendors
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openDrawer(company)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteCompany(company.id)}
                          className="text-red-600 hover:text-red-900 p-1"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Drawer */}
      {showDrawer && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDrawer}></div>
          
          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <div className="w-screen max-w-md">
              <div className="h-full flex flex-col bg-white shadow-xl">
                {/* Drawer header */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">
                      {editingCompany ? 'Edit Company' : 'Add Company'}
                    </h2>
                    <button
                      onClick={closeDrawer}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <span className="sr-only">Close</span>
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Drawer content */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <form className="space-y-6">
                    {/* Company Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.name ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter company name"
                      />
                      {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                    </div>

                    {/* Address Line 1 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Address Line 1 *
                      </label>
                      <input
                        type="text"
                        value={formData.address1}
                        onChange={(e) => handleInputChange('address1', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.address1 ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter address line 1"
                      />
                      {errors.address1 && <p className="mt-1 text-sm text-red-600">{errors.address1}</p>}
                    </div>

                    {/* Address Line 2 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Address Line 2
                      </label>
                      <input
                        type="text"
                        value={formData.address2}
                        onChange={(e) => handleInputChange('address2', e.target.value)}
                        className="mt-1 block w-full border rounded-md px-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter address line 2"
                      />
                    </div>

                    {/* State */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        State *
                      </label>
                      <select
                        value={formData.state}
                        onChange={(e) => {
                          handleInputChange('state', e.target.value);
                          // Clear city when state changes
                          handleInputChange('city', '');
                        }}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.state ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                      >
                        <option value="">Select State</option>
                        {states.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                      {errors.state && <p className="mt-1 text-sm text-red-600">{errors.state}</p>}
                    </div>

                    {/* City */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        City *
                      </label>
                      <select
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.city ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        disabled={!formData.state}
                      >
                        <option value="">Select City</option>
                        {formData.state && getCitiesByState(formData.state).map((city) => (
                          <option key={city.id} value={city.City}>
                            {city.City}
                          </option>
                        ))}
                      </select>
                      {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
                    </div>

                    {/* Pin Code */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Pin Code *
                      </label>
                      <input
                        type="text"
                        value={formData.pinCode}
                        onChange={(e) => handleInputChange('pinCode', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.pinCode ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter pin code"
                      />
                      {errors.pinCode && <p className="mt-1 text-sm text-red-600">{errors.pinCode}</p>}
                    </div>

                    {/* Contact Person */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Contact Person *
                      </label>
                      <input
                        type="text"
                        value={formData.contactPerson}
                        onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.contactPerson ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter contact person name"
                      />
                      {errors.contactPerson && <p className="mt-1 text-sm text-red-600">{errors.contactPerson}</p>}
                    </div>

                    {/* Contact Phone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Contact Phone *
                      </label>
                      <input
                        type="tel"
                        value={formData.contactPhone}
                        onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.contactPhone ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter contact phone number"
                      />
                      {errors.contactPhone && <p className="mt-1 text-sm text-red-600">{errors.contactPhone}</p>}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.email ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter email address"
                      />
                      {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                    </div>

                    {/* Rotation Mode */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Rotation Mode *
                      </label>
                      <select
                        value={formData.rotationMode}
                        onChange={(e) => handleInputChange('rotationMode', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        {rotationModes.map((mode) => (
                          <option key={mode.value} value={mode.value}>
                            {mode.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Approved Vendors */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Approved Vendors *
                      </label>
                      <select
                        multiple
                        value={formData.approvedVendors}
                        onChange={(e) => {
                          const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                          handleInputChange('approvedVendors', selectedOptions);
                        }}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.approvedVendors ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        size="4"
                      >
                        {vendors.map((vendor) => (
                          <option key={vendor.id} value={vendor.id}>
                            {vendor.name}
                          </option>
                        ))}
                      </select>
                      {errors.approvedVendors && <p className="mt-1 text-sm text-red-600">{errors.approvedVendors}</p>}
                    </div>

                    {/* Meal Schedule */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Meal Schedule
                      </label>
                      <div className="mt-2 space-y-3">
                        {Object.entries(formData.mealSchedule).map(([meal, time]) => (
                          <div key={meal} className="flex items-center space-x-3">
                            <label className="text-sm text-gray-600 capitalize w-20">
                              {meal}:
                            </label>
                            <input
                              type="time"
                              value={time}
                              onChange={(e) => handleMealScheduleChange(meal, e.target.value)}
                              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Vendor Rotation by Day (only for customDays mode) */}
                    {formData.rotationMode === 'customDays' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Vendor Rotation by Day
                        </label>
                        <div className="mt-2 space-y-3">
                          {daysOfWeek.map((day) => (
                            <div key={day} className="flex items-center space-x-3">
                              <label className="text-sm text-gray-600 w-20">
                                {day}:
                              </label>
                              <select
                                value={formData.vendorRotationByDay[day] || ''}
                                onChange={(e) => handleVendorRotationChange(day, e.target.value)}
                                className="flex-1 border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="">Select vendor</option>
                                {vendors.map((vendor) => (
                                  <option key={vendor.id} value={vendor.id}>
                                    {vendor.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </form>
                </div>

                {/* Drawer footer */}
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={closeDrawer}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={saveCompany}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      {editingCompany ? 'Update' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealDeskCompaniesManager; 